import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  CheckmarkCircle02Icon,
  QrCodeIcon,
  Ticket02Icon,
  Wallet02Icon,
} from 'hugeicons-react';
import GeneralError from '@components/GeneralError.component';
import Heading from '@components/shared/Heading.component';
import Badge from '@components/ui/Badge.component';
import GlassCard from '@components/ui/GlassCard/GlassCard';
import ArrowSvg from '@components/ui/icons/Arrow';
import ProgressBar from '@components/ui/ProgressBar';
import EventLayout from '@layouts/Event.layout';
import { useQrSummationData } from '@stores/qrSummation.store';
import { useUser } from '@stores/user.store';

function ProgressPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    checkMeta,
    isQrCodeLoading: showPreloader,
    qrError,
  } = useQrSummationData();

  const { user } = useUser();

  const {
    tickets_num,
    next_tickets_num,
    total_sum,
    total_value,
    next_min_sum,
  } = checkMeta;

  const mainButtonParams = {
    text: t('main.mainButton'),
    isEnabled: !showPreloader,
    isVisible: !showPreloader,
    onClick: () => navigate('/'),
  };

  const secondaryButtonParams = {
    text: t('common.rules'),
    isEnabled: !showPreloader,
    isVisible: !showPreloader,
  };

  if (qrError) {
    const errorText = qrError[user.lang] ?? t('errors.somethingWentWrong');
    return (
      <GeneralError
        onButtonClick={() => navigate('/')}
        showPreloader={showPreloader}
        errorText={errorText}
      />
    );
  }

  return (
    <EventLayout
      showPreloader={showPreloader}
      mainButtonParams={mainButtonParams}
      secondaryButtonParams={secondaryButtonParams}
    >
      <section className={'my-3 p-3 flex flex-col gap-2'}>
        <div className="flex flex-row items-center justify-center">
          <div className="flex-shrink-0 bg-event-icon-background/70 p-2 rounded-full">
            <CheckmarkCircle02Icon size={48} className="text-corporate-light" />
          </div>
        </div>
        <Heading>{t('main.header')}</Heading>
        <div className="flex flex-row items-center justify-center">
          <Badge
            className={
              'px-3 py-2.5 mx-auto items-center justify-center gap-1 font-wide w-fit shadow-sm'
            }
            transparency={false}
            textSize={'text-md'}
            variant={'full'}
            text={`${total_value} MDL`}
            icon={<QrCodeIcon size={24} strokeWidth={2} />}
          />
        </div>
      </section>

      <GlassCard className="py-3.5 px-5 rounded-2xl flex flex-col gap-3 max-w-full sm:max-w-3/4 md:max-w-1/2 lg:max-w-1/3 2xl:max-w-1/4 mx-auto shadow-sm">
        <div className="flex flex-row w-full items-center justify-center rounded-2xl">
          <h2 className="text-corporate-light text-xl text-nowrap">
            {t('progress.section.title')}
          </h2>
        </div>
        <div className="flex flex-row items-center justify-between mb-5">
          <Badge
            variant={'full'}
            className={
              'py-2.5 px-3 items-center justify-center gap-1 font-wide w-fit shadow'
            }
            textSize={'text-xl'}
            text={`${total_sum} MDL`}
            icon={<Wallet02Icon size={24} strokeWidth={2} />}
          />
          <Badge
            variant={'transparent'}
            className={'p-2.5 items-center justify-center gap-1 font-wide'}
            textSize={'text-3xl'}
            text={tickets_num}
            icon={<Ticket02Icon size={38} />}
          />
        </div>
        <div className="flex flex-row items-center justify-between min-h-1/2 text-corporate-light">
          <div className="grid-rows-2 text-left flex flex-col">
            <span className="text-xl text-event-muted text-nowrap">
              {t('progress.section.require_money')}
            </span>
            <span className="text-xl font-wide">{next_min_sum} MDL</span>
          </div>
          <div className="grid-rows-1">
            <ArrowSvg />
          </div>
          <div className="grid-rows-2 text-right justify-end flex flex-col">
            <span className="text-xl text-event-muted text-nowrap">
              {t('progress.section.receive_tickets')}
            </span>
            <Badge
              variant={'transparent'}
              textSize={'text-xl'}
              className={'items-center justify-end font-wide m-0 gap-0 '}
              icon={<Ticket02Icon size={24} />}
              text={next_tickets_num}
            />
          </div>
        </div>
        <ProgressBar
          animate
          fillDuration={2300}
          max={500}
          className={'min-h-3'}
          showSummary={false}
          showTooltip={false}
          showPoints={false}
          remaining={next_min_sum}
        />
      </GlassCard>
    </EventLayout>
  );
}

export default ProgressPage;
